import React, { useState, useEffect, useMemo, useRef } from 'react';
import { P, font, mono } from '../styles/theme';
import { Spinner, Card } from '../components/Shared';
import { CongTyService } from '../services/api';

/**
 * Tra cứu KSK theo công ty (luồng admin / HR).
 * - Dropdown công ty (searchable) từ API /kskresults/congty
 * - Lọc theo khoảng ngày khám
 * - Bảng nhân viên KSK, click 1 dòng → mở kết quả (ma_luotkham)
 */
export default function AdminCongTyPage({ onBack, onSelectVisit }) {
  const [companies, setCompanies] = useState([]);
  const [loadingCty, setLoadingCty] = useState(true);
  const [selected, setSelected] = useState(null);      // company object
  const [search, setSearch] = useState('');            // combobox search text
  const [open, setOpen] = useState(false);             // dropdown open
  const [tuNgay, setTuNgay] = useState('');
  const [denNgay, setDenNgay] = useState('');
  const [rows, setRows] = useState([]);
  const [loadingNv, setLoadingNv] = useState(false);
  const [error, setError] = useState('');
  const [filterNv, setFilterNv] = useState('');        // lọc nhanh trong bảng
  const boxRef = useRef(null);

  // Load danh mục công ty
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await CongTyService.list();
        if (!cancelled) setCompanies(data || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 403
          ? 'Bạn không có quyền xem danh mục công ty.'
          : 'Không thể tải danh mục công ty. ' + (err.response?.data?.message || ''));
      } finally {
        if (!cancelled) setLoadingCty(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Đóng dropdown khi click ra ngoài
  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(c =>
      (c.tenKhachHang || '').toLowerCase().includes(q) ||
      (c.maKhachHang || '').toLowerCase().includes(q) ||
      (c.maSoThue || '').toLowerCase().includes(q));
  }, [companies, search]);

  const loadEmployees = async (company, from = tuNgay, to = denNgay) => {
    if (!company) return;
    setLoadingNv(true); setError(''); setRows([]);
    try {
      const data = await CongTyService.employees(company.idKhachHang, from, to);
      setRows(data || []);
    } catch (err) {
      setError(err.response?.status === 403
        ? 'Bạn không có quyền xem dữ liệu công ty này.'
        : 'Không thể tải danh sách nhân viên. ' + (err.response?.data?.message || ''));
    } finally {
      setLoadingNv(false);
    }
  };

  const pickCompany = (c) => {
    setSelected(c);
    setSearch(c.tenKhachHang);
    setOpen(false);
    loadEmployees(c);
  };

  const filteredRows = useMemo(() => {
    const q = filterNv.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.tenBenhNhan || '').toLowerCase().includes(q) ||
      (r.boPhan || '').toLowerCase().includes(q) ||
      (r.maLuotKham || '').toLowerCase().includes(q));
  }, [rows, filterNv]);

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    borderRadius: 8, border: '1px solid ' + P.border, background: P.inputBg,
    fontSize: 14, fontFamily: font, color: P.text, outline: 'none',
  };

  const th = { padding: '11px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: P.textDim, whiteSpace: 'nowrap', borderBottom: '2px solid ' + P.border };
  const td = { padding: '11px 12px', fontSize: 13, color: P.text, borderBottom: '1px solid ' + P.border };

  return (
    <div style={{ minHeight: '100vh', background: P.bg, fontFamily: font, color: P.text }}>

      {/* Header */}
      <header className="app-header" style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: P.headerBg, borderBottom: '2px solid ' + P.primaryDark,
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 2px 8px rgba(0,114,188,0.15)',
      }}>
        <button onClick={onBack} title="Quay lại" style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
          color: P.headerText, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <div>
          <div className="header-title" style={{ fontSize: 15, fontWeight: 700, color: P.headerText }}>PHÒNG KHÁM ĐA KHOA KCN QUẾ VÕ</div>
          <div className="header-sub" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>Tra cứu khám sức khỏe theo công ty</div>
        </div>
      </header>

      <div className="page-content" style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px 60px' }}>

        {/* Bộ lọc */}
        <Card style={{ padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>

            {/* Combobox công ty */}
            <div ref={boxRef} style={{ flex: '2 1 340px', position: 'relative' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 }}>
                Công ty / khách hàng KSK <span style={{ color: P.danger }}>*</span>
              </label>
              <input
                style={inputStyle}
                placeholder={loadingCty ? 'Đang tải danh mục...' : 'Gõ tên, mã hoặc MST để tìm công ty...'}
                value={search}
                disabled={loadingCty}
                onChange={e => { setSearch(e.target.value); setOpen(true); if (selected) setSelected(null); }}
                onFocus={() => setOpen(true)}
              />
              {open && !loadingCty && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  background: P.white, border: '1px solid ' + P.border, borderRadius: 8,
                  marginTop: 4, maxHeight: 280, overflowY: 'auto',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}>
                  {filteredCompanies.length === 0 && (
                    <div style={{ padding: '12px 14px', fontSize: 13, color: P.textMuted }}>Không tìm thấy công ty phù hợp</div>
                  )}
                  {filteredCompanies.map(c => (
                    <div key={c.idKhachHang}
                      onClick={() => pickCompany(c)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid ' + P.border }}
                      onMouseEnter={e => e.currentTarget.style.background = P.primaryLight}
                      onMouseLeave={e => e.currentTarget.style.background = P.white}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600, color: P.text }}>{c.tenKhachHang}</div>
                      <div style={{ fontSize: 12, color: P.textMuted, fontFamily: mono }}>
                        {c.maKhachHang}{c.maSoThue ? ' • MST: ' + c.maSoThue : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Khoảng ngày khám */}
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 }}>Từ ngày</label>
              <input type="date" style={inputStyle} value={tuNgay} onChange={e => setTuNgay(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: P.textDim, marginBottom: 6 }}>Đến ngày</label>
              <input type="date" style={inputStyle} value={denNgay} onChange={e => setDenNgay(e.target.value)} />
            </div>

            <button
              onClick={() => loadEmployees(selected)}
              disabled={!selected || loadingNv}
              style={{
                padding: '10px 22px', borderRadius: 8, border: 'none',
                background: !selected ? P.borderLight : P.primary, color: P.white,
                fontSize: 14, fontWeight: 600, fontFamily: font,
                cursor: !selected ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, height: 41,
              }}>
              {loadingNv ? <Spinner size={16} color={P.white} /> : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
              Tra cứu
            </button>
          </div>
        </Card>

        {/* Error */}
        {error && (
          <Card style={{ padding: '30px 24px', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
            <p style={{ color: P.danger, fontSize: 14, margin: 0 }}>{error}</p>
          </Card>
        )}

        {/* Loading */}
        {loadingNv && (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spinner size={36} />
            <p style={{ color: P.textDim, marginTop: 14, fontSize: 14 }}>Đang tải danh sách nhân viên...</p>
          </div>
        )}

        {/* Kết quả */}
        {!loadingNv && selected && !error && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid ' + P.border,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: P.primary }}>
                {selected.tenKhachHang}
                <span style={{ fontWeight: 400, color: P.textMuted, marginLeft: 10, fontSize: 13 }}>
                  {rows.length} nhân viên{filterNv ? ` • lọc: ${filteredRows.length}` : ''}
                </span>
              </div>
              <input
                style={{ ...inputStyle, width: 260 }}
                placeholder="Lọc theo tên, bộ phận, mã lượt khám..."
                value={filterNv}
                onChange={e => setFilterNv(e.target.value)}
              />
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: P.textDim, fontSize: 14 }}>
                Chưa có dữ liệu KSK cho công ty này{tuNgay || denNgay ? ' trong khoảng ngày đã chọn' : ''}.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: P.inputBg }}>
                      <th style={th}>#</th>
                      <th style={th}>Họ tên</th>
                      <th style={th}>Ngày sinh</th>
                      <th style={th}>Giới tính</th>
                      <th style={th}>Bộ phận</th>
                      <th style={th}>Ngày khám</th>
                      <th style={th}>Phân loại SK</th>
                      <th style={th}>Bệnh tật</th>
                      <th style={th}>Kết luận</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, i) => (
                      <tr key={(r.maLuotKham || '') + i}
                        onClick={() => r.maLuotKham && onSelectVisit(r.maLuotKham)}
                        style={{ cursor: r.maLuotKham ? 'pointer' : 'default', transition: 'background .15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = P.primaryLight}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ ...td, fontFamily: mono, color: P.textMuted }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {r.tenBenhNhan}
                          <div style={{ fontSize: 11, fontWeight: 400, color: P.textMuted, fontFamily: mono }}>{r.maLuotKham}</div>
                        </td>
                        <td style={td}>{r.ngaySinh || '—'}</td>
                        <td style={td}>{r.gioiTinh || '—'}</td>
                        <td style={td}>{r.boPhan || '—'}</td>
                        <td style={td}>{r.ngayKham || '—'}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {r.phanLoaiSucKhoe
                            ? <span style={{
                                display: 'inline-block', minWidth: 28, padding: '3px 8px', borderRadius: 6,
                                background: P.primaryLight, color: P.primary, fontWeight: 700, fontSize: 12,
                              }}>{r.phanLoaiSucKhoe}</span>
                            : '—'}
                        </td>
                        <td style={{ ...td, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.benhTat}>
                          {r.benhTat || '—'}
                        </td>
                        <td style={td}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                            background: r.daKetLuan ? '#e8f8ef' : '#fdf3e7',
                            color: r.daKetLuan ? P.success : P.warning,
                          }}>
                            {r.daKetLuan ? 'Đã kết luận' : 'Chưa kết luận'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Chưa chọn công ty */}
        {!loadingNv && !selected && !error && (
          <Card style={{ padding: '50px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>🏢</div>
            <p style={{ color: P.textDim, fontSize: 15, margin: 0 }}>
              Chọn công ty ở ô tìm kiếm phía trên để xem danh sách khám sức khỏe.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
